import isNil from "lodash/isNil";
import { usePotentialWarningsQueryInvalidate } from "PFApp/booking/components/booking_form/use_potential_warnings/use_potential_warnings_query_invalidate";
import { useRefreshBookingData } from "PFApp/booking/hooks/use_refresh_booking_data";
import { useSyncRows } from "PFApp/booking/parts/overview/use_sync_rows";
import { updateBooking as updateBookingRequest } from "PFApp/booking/services/api";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { utc } from "PFCore/helpers/date";
import { useBookingInvalidate } from "PFCore/hooks/queries";
import { useProfileInvalidate } from "PFCore/hooks/queries/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Booking } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

const DRAGGING_BOOKING_SUCCESS_ACTION_ID = getGrowlId("bm-dragging-booking");

interface Props {
  booking: Booking;
  onError: () => void;
  onLoadingChange: (value: boolean) => void;
}

const useUpdateBooking = ({ booking, onError, onLoadingChange }: Props) => {
  const { showGrowl } = useGrowlStoreActions();
  const syncRows = useSyncRows();
  const { formatISODateTime } = useDateFormatter();
  const refreshBookingData = useRefreshBookingData();
  const { t } = useTranslation("bookingModule");
  const { invalidateProfileWithBooking } = useProfileInvalidate();
  const { invalidate: invalidateBooking } = useBookingInvalidate();
  const { invalidateAll: invalidatePotentialWarnings } = usePotentialWarningsQueryInvalidate();

  const updateBooking = useCallback(
    async ({ startDate, endDate }) => {
      if (!booking) {
        return;
      }
      try {
        onLoadingChange(true);
        // regular API call instead of react-query mutation hook to improve performance
        await updateBookingRequest({
          id: booking.id,
          startDate: formatISODateTime(utc(startDate)),
          endDate: formatISODateTime(utc(endDate))
        });
        refreshBookingData({
          bookingIds: [booking?.id],
          bookingGroupId: booking?.booking_group_id,
          activityId: booking?.activity?.id,
          profileIds: booking?.profile_id ? [booking.profile_id] : undefined,
          refreshProfile: !isNil(booking.booking_template_id)
        });
        invalidateProfileWithBooking(booking.id);
        invalidateBooking(booking.id);
        invalidatePotentialWarnings();

        const parentActivityIds = booking.activity?.parent_activity?.id
          ? [booking.activity.parent_activity.id]
          : [];
        await syncRows({
          profileId: booking.profile_id,
          activityId: booking.activity?.id,
          parentActivityIds: parentActivityIds as any
        });

        await new Promise<void>((resolve) =>
          setTimeout(() => {
            onLoadingChange(false);
            showGrowl({
              id: DRAGGING_BOOKING_SUCCESS_ACTION_ID,
              kind: "success",
              message: t("bookings.update.success"),
              hidePreviousByTypeAndId: true,
              ttl: 5000
            });
            resolve();
          }, 200)
        ); // give the chart some time to update;
      } catch {
        onLoadingChange(false);
        showGrowl({
          kind: "error",
          message: t("bookings.update.error")
        });
        onError();
      }
    },
    [
      booking,
      formatISODateTime,
      showGrowl,
      invalidateBooking,
      invalidatePotentialWarnings,
      invalidateProfileWithBooking,
      onError,
      onLoadingChange,
      refreshBookingData,
      syncRows,
      t
    ]
  );

  return { updateBooking };
};

export default useUpdateBooking;
